// src/data/testimonials.ts

export type Testimonial = {
  id: string;
  quote: string;
  source: string;
  role?: string;
  year?: string;
  context?: string;
};

export type TestimonialGroup = {
  id: string; // keep this loose so other code can't complain
  title: string;
  subtitle: string;
  accent?: string;
  moreLink?: string;
  testimonials: Testimonial[];
};

export const testimonialGroups: TestimonialGroup[] = [
  {
    id: "everyday",
    title: "Everyday Calm & Anxiety",
    subtitle: "Real people using Samboards for sleep, anxiety, focus and day-to-day regulation.",
    accent: "from-emerald-400 to-cyan-400",
    moreLink: "/feedback/dementia-aging",
    testimonials: [
      {
        id: "everyday-1",
        quote:
          "I use my Samboards in the office, at work, and in meetings. It helps with my concentration and lowers my stress levels. I pick it up and use it way more than I thought I would.",
        source: "Customer",
        year: "2021",
      },
      {
        id: "everyday-2",
        quote:
          "Now I am working from home, I use mine during online meetings – no one can see me using it under the table.",
        source: "Customer",
        year: "2021",
      },
      {
        id: "everyday-3",
        quote: "I use it every night. It helps me relax so I sleep well. It's a very good product.",
        source: "Claudia",
        year: "2021",
      },
      {
        id: "everyday-4",
        quote:
          "I used a set of three with an adult with learning difficulties and mental health issues. He uses it most when he's feeling bad and wants to break something – it helps him think about nice things instead of the bad things.",
        source: "Support worker",
        year: "2020",
        context: "Adult with learning difficulties and mental health issues",
      },
      {
        id: "everyday-5",
        quote: "If I have something in my hands it's easier to deal with the strange and stressful stuff going on now.",
        source: "Customer",
        year: "2020",
      },
      {
        id: "everyday-6",
        quote:
          "I'm doing online courses to keep busy; I fidget with my board all the time. The sharp bit keeps me alert when I need to focus.",
        source: "Customer",
        year: "2020",
      },
      {
        id: "everyday-7",
        quote: "How can something so simple be so satisfying?",
        source: "Customer",
        year: "2020",
      },
      {
        id: "everyday-8",
        quote:
          "This lives on my keys and helps me so much when I'm out shopping and feel an anxiety attack coming on. Highly recommended for all ages.",
        source: "Customer",
        year: "2020",
      },
      {
        id: "everyday-9",
        quote:
          "These boards have really changed my life. I go to bed without trying to put it off and without worrying about panic attacks. I always keep one in my pocket for any time I feel anxious.",
        source: "Customer",
        year: "2020",
        context: "Elderly user",
      },
      {
        id: "everyday-10",
        quote:
          "The boards have really helped my nan's breathing exercises. When her oxygen dropped, we used the Samboard and her readings went up. It distracts her panic and it's by her side every single day.",
        source: "Family member",
        year: "2020",
        context: "COPD and multiple health issues",
      },
      {
        id: "everyday-11",
        quote:
          "I have always struggled with anxiety and sleep. During the pandemic it got worse. I started using Samboards during the day and at night – the first night I used it, I didn't have a panic attack. I haven't had one since.",
        source: "Customer",
        year: "2020",
      },
    ],
  },
  {
    id: "nhs",
    title: "NHS & Healthcare",
    subtitle: "How NHS staff and clinical teams use Samboards in hospitals, wards and clinics.",
    accent: "from-sky-400 to-indigo-400",
    moreLink: "/feedback/nhs",
    testimonials: [
      {
        id: "nhs-1",
        quote:
          "Staff found Samboards portable, engaging and helpful for reducing stress, focusing on goals and providing anxiety relief.",
        source: "Insight Consultancy summary",
        year: "2021",
      },
      {
        id: "nhs-2",
        quote:
          "Working in a hospital under current circumstances is particularly stressful. The board helps take my mind off a million thoughts and sew a line of thinking back together.",
        source: "NHS Staff",
        year: "2021",
      },
      {
        id: "nhs-3",
        quote:
          "I have mild ADHD and it helped me focus on goals and targets one step at a time without drifting into a million outstanding tasks.",
        source: "NHS Staff",
        year: "2021",
      },
      {
        id: "nhs-4",
        quote:
          "Used the prickly tile during a presentation I had to give. It distracted from the nerves of presenting and I felt incredibly relaxed.",
        source: "NHS Staff",
        year: "2021",
      },
      {
        id: "nhs-5",
        quote:
          "Used in work to reduce my stress levels when getting into situations that would normally create anxiety. Having it on my lanyard meant it was always there as a distraction.",
        source: "NHS Staff",
        year: "2021",
      },
      {
        id: "nhs-6",
        quote:
          "Given one to my youngest child during home schooling to help him focus and keep still. On days he had the tile his concentration improved and he was less inclined to get up out of his chair.",
        source: "NHS Staff",
        year: "2021",
      },
      {
        id: "nhs-mba-study",
        quote:
          "NHS staff used Samboards to manage stress, focus, and presentation anxiety during COVID. Read full verbatim feedback below.",
        source: "MBA NHS frontline study (2021)",
        role: "Research study",
        context: "Extracts from an MBA research project during the COVID peak.",
        year: "2021",
      },
    ],
  },
  {
    id: "headway",
    title: "Headway & Neurological Rehab",
    subtitle: "Feedback from Headway organisations supporting brain injury and stroke survivors.",
    accent: "from-amber-400 to-orange-400",
    moreLink: "/feedback/headway",
    testimonials: [
      {
        id: "headway-1",
        quote: "Yes, 100% – I enjoyed using Samboards very much.",
        source: "Headway Staff",
        year: "2020",
      },
      {
        id: "headway-2",
        quote: "The different textures felt soft and pleasant.",
        source: "Headway Staff",
        year: "2020",
      },
      {
        id: "headway-3",
        quote: "Feeling the many bumps and ridges was enjoyable because of my neurological condition.",
        source: "Headway Client",
        year: "2020",
      },
      {
        id: "headway-4",
        quote: "Sliding my hand on the swirl and wave shape was very enjoyable.",
        source: "Headway Client",
        year: "2020",
      },
      {
        id: "headway-5",
        quote: "Refocuses the mind, instant reward. I'd rate the board 9 out of 10.",
        source: "Headway Client",
        year: "2020",
      },
    ],
  },
  {
    id: "sendco",
    title: "Schools, SEND & Education",
    subtitle: "How SENDCOs and teachers use Samboards to support children in school.",
    accent: "from-pink-400 to-fuchsia-400",
    moreLink: "/feedback/sendco",
    testimonials: [
      {
        id: "sendco-1",
        quote:
          "We have given them to a few children with anxiety who use them to soothe. The children like using them.",
        source: "SENDCO",
        role: "Early Years Teacher",
        year: "2020",
      },
      {
        id: "sendco-2",
        quote:
          "One child picks her nails and the skin around them when anxious – the board helps redirect that behaviour.",
        source: "SENDCO",
        year: "2020",
      },
      {
        id: "sendco-3",
        quote:
          "We use it for a child who regularly makes vocal noises in class during whole-class teaching. The board helps distract him from making the noises.",
        source: "SENDCO",
        year: "2020",
      },
    ],
  },
];
